// src/utils/formatPrice.js
// Utilitaires pour formater les prix - NE PAS MODIFIER

/**
 * Formatter un prix en CFA avec séparateurs de milliers
 * @param {number} price - Prix en CFA
 * @param {string} currency - Devise (défaut: 'FCFA')
 * @returns {string} Prix formaté (ex: "125 000 FCFA")
 */
export const formatPrice = (price, currency = 'FCFA') => {
    if (price === null || price === undefined) return 'N/A';
    
    const numPrice = Number(price);
    if (isNaN(numPrice)) return 'N/A';
  
    const formatted = numPrice.toLocaleString('fr-FR', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
  
    return `${formatted} ${currency}`;
  };
  
  /**
   * Formatter un prix avec le type de tarification (par nuit, par mois, par m²)
   * @param {number} price - Prix
   * @param {string} priceType - Type: 'perNight', 'perMonth', 'perSquareMeter', 'service'
   * @param {string} language - 'fr' ou 'en'
   * @returns {string} Prix avec unité (ex: "125 000 FCFA/nuit")
   */
  export const formatPriceWithUnit = (price, priceType = 'service', language = 'fr') => {
    const basePrice = formatPrice(price);
  
    const units = {
      fr: {
        perNight: '/nuit',
        perMonth: '/mois',
        perSquareMeter: '/m²',
        service: 'FCFA',
      },
      en: {
        perNight: '/night',
        perMonth: '/month',
        perSquareMeter: '/m²',
        service: 'FCFA',
      },
    };
  
    const unit = units[language]?.[priceType] || units.fr[priceType];
    return `${basePrice}${unit}`;
  };
  
  /**
   * Formatter un prix de terrain (prix au m² + superficie)
   * @param {number} pricePerM2 - Prix par m²
   * @param {number} areaM2 - Superficie en m²
   * @returns {string} Affichage formaté (ex: "2 500 FCFA/m² · 500 m²")
   */
  export const formatTerrainPrice = (pricePerM2, areaM2) => {
    const priceFormatted = formatPrice(pricePerM2);
    const areaFormatted = Number(areaM2).toLocaleString('fr-FR');
    return `${priceFormatted}/m² · ${areaFormatted} m²`;
  };
  
  /**
   * Calculer le prix total d'une réservation
   * @param {number} pricePerNight - Prix par nuit
   * @param {Date|string} checkInDate - Date d'arrivée
   * @param {Date|string} checkOutDate - Date de départ
   * @returns {object} { nights: number, totalPrice: number, formatted: string }
   */
  export const calculateTotalPrice = (pricePerNight, checkInDate, checkOutDate) => {
    if (!pricePerNight || !checkInDate || !checkOutDate) {
      return { nights: 0, totalPrice: 0, formatted: 'N/A' };
    }
  
    const start = new Date(checkInDate);
    const end = new Date(checkOutDate);
    const nights = Math.ceil((end - start) / (1000 * 60 * 60 * 24));
  
    if (nights <= 0) {
      return { nights: 0, totalPrice: 0, formatted: 'N/A' };
    }
  
    const totalPrice = pricePerNight * nights;
  
    return {
      nights,
      totalPrice,
      formatted: formatPrice(totalPrice),
    };
  };
  
  /**
   * Calculer le prix total d'une location mensuelle
   * @param {number} pricePerMonth - Prix par mois
   * @param {number} months - Nombre de mois
   * @returns {object} { months: number, totalPrice: number, formatted: string }
   */
  export const calculateMonthlyRentalPrice = (pricePerMonth, months = 1) => {
    if (!pricePerMonth || months <= 0) {
      return { months: 1, totalPrice: 0, formatted: 'N/A' };
    }
  
    const totalPrice = pricePerMonth * months;
  
    return {
      months,
      totalPrice,
      formatted: formatPrice(totalPrice),
    };
  };
  
  /**
   * Calculer le prix total d'un terrain
   * @param {number} pricePerM2 - Prix par m²
   * @param {number} areaM2 - Superficie en m²
   * @returns {object} { areaM2: number, totalPrice: number, formatted: string }
   */
  export const calculateTerrainTotalPrice = (pricePerM2, areaM2) => {
    if (!pricePerM2 || !areaM2) {
      return { areaM2: 0, totalPrice: 0, formatted: 'N/A' };
    }
  
    const totalPrice = pricePerM2 * areaM2;
  
    return {
      areaM2,
      totalPrice,
      formatted: formatPrice(totalPrice),
    };
  };
  
  /**
   * Parser un prix depuis une chaîne (ex: "125000" ou "125 000" ou "125000 FCFA")
   * @param {string} priceString - Chaîne de prix
   * @returns {number} Prix en nombre
   */
  export const parsePrice = (priceString) => {
    if (!priceString) return 0;
  
    // Enlever les espaces et la devise
    const cleaned = String(priceString)
      .replace(/\s+/g, '')
      .replace(/[^\d]/g, '');
  
    const price = Number(cleaned);
    return isNaN(price) ? 0 : price;
  };
  
  /**
   * Comparer deux prix
   * @param {number} price1 - Premier prix
   * @param {number} price2 - Deuxième prix
   * @returns {object} { difference: number, percentage: number, cheaper: string }
   */
  export const comparePrices = (price1, price2) => {
    if (!price1 || !price2) return null;
  
    const difference = price2 - price1;
    const percentage = ((difference / price1) * 100).toFixed(1);
    const cheaper = difference > 0 ? 'price2' : difference < 0 ? 'price1' : 'equal';
  
    return {
      difference: Math.abs(difference),
      percentage: Math.abs(percentage),
      cheaper,
      formatted: {
        difference: formatPrice(Math.abs(difference)),
        percentage: `${Math.abs(percentage)}%`,
      },
    };
  };
  
  /**
   * Obtenir une gamme de prix (économique, moyen, haut de gamme)
   * @param {number} price - Prix
   * @param {string} category - Catégorie ('sejours', 'locations', etc.)
   * @returns {string} Gamme ('economy', 'mid', 'luxury')
   */
  export const getPriceRange = (price, category = 'locations') => {
    if (!price || price <= 0) return 'economy';
  
    // Seuils par catégorie
    const thresholds = {
      sejours: { economy: 50000, luxury: 150000 },
      locations: { economy: 200000, luxury: 500000 },
      immobilier: { economy: 50000000, luxury: 200000000 },
      services: { economy: 100000, luxury: 500000 },
    };
  
    const categoryThresholds = thresholds[category] || thresholds.locations;
  
    if (price <= categoryThresholds.economy) return 'economy';
    if (price <= categoryThresholds.luxury) return 'mid';
    return 'luxury';
  };
  
  /**
   * Obtenir une description textuelle de la gamme de prix
   * @param {number} price - Prix
   * @param {string} category - Catégorie
   * @param {string} language - 'fr' ou 'en'
   * @returns {string} Description (ex: "Économique")
   */
  export const getPriceRangeLabel = (price, category = 'locations', language = 'fr') => {
    const range = getPriceRange(price, category);
  
    const labels = {
      fr: {
        economy: 'Économique',
        mid: 'Moyen de gamme',
        luxury: 'Haut de gamme',
      },
      en: {
        economy: 'Budget',
        mid: 'Mid-range',
        luxury: 'Luxury',
      },
    };
  
    return labels[language]?.[range] || labels.fr[range];
  };
  
  /**
   * Formater le prix pour l'affichage sur une carte
   * @param {number} price - Prix
   * @param {string} priceType - Type: 'perNight', 'perMonth', 'perSquareMeter'
   * @returns {string} Prix court (ex: "125K" ou "2.5M")
   */
  export const formatPriceForMap = (price, priceType = 'perMonth') => {
    if (!price || price <= 0) return 'N/A';
  
    let formatted;
    if (price >= 1000000) {
      formatted = (price / 1000000).toFixed(1) + 'M';
    } else if (price >= 1000) {
      formatted = (price / 1000).toFixed(0) + 'K';
    } else {
      formatted = price.toString();
    }
  
    const suffix = {
      perNight: '/n',
      perMonth: '/m',
      perSquareMeter: '/m²',
      service: '',
    }[priceType] || '';
  
    return `${formatted}${suffix}`;
  };
  
  /**
   * Calculer la réduction ou surcharge
   * @param {number} originalPrice - Prix original
   * @param {number} discountedPrice - Prix réduit
   * @returns {object} { discount: number, percentage: number, formatted: string }
   */
  export const calculateDiscount = (originalPrice, discountedPrice) => {
    if (!originalPrice || !discountedPrice) return null;
  
    if (discountedPrice >= originalPrice) return null; // Pas de réduction
  
    const discount = originalPrice - discountedPrice;
    const percentage = ((discount / originalPrice) * 100).toFixed(0);
  
    return {
      discount,
      percentage: Number(percentage),
      formatted: {
        discount: formatPrice(discount),
        percentage: `${percentage}%`,
        discountedPrice: formatPrice(discountedPrice),
      },
    };
  };
  
  /**
   * Convertir les prix entre devises (approximation)
   * @param {number} price - Prix en FCFA
   * @param {string} targetCurrency - Devise cible ('USD', 'EUR', 'GBP')
   * @returns {number} Prix converti
   */
  export const convertCurrency = (price, targetCurrency = 'USD') => {
    if (!price || price <= 0) return 0;
  
    // Taux de change approximatif (1 EUR = 655 FCFA)
    const rates = {
      USD: 1 / 600, // Approximatif
      EUR: 1 / 655,
      GBP: 1 / 750,
      XOF: 1, // FCFA
      FCFA: 1,
    };
  
    const rate = rates[targetCurrency] || rates.USD;
    return Math.round(price * rate * 100) / 100;
  };
  
  /**
   * Formater un prix avec devise convertie
   * @param {number} price - Prix en FCFA
   * @param {string} targetCurrency - Devise cible
   * @returns {string} Prix converti formaté
   */
  export const formatConvertedPrice = (price, targetCurrency = 'USD') => {
    const converted = convertCurrency(price, targetCurrency);
    return converted.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }) + ` ${targetCurrency}`;
  };
  
  /**
   * Générer un résumé de prix pour l'affichage
   * @param {object} listing - Objet annonce
   * @param {string} language - 'fr' ou 'en'
   * @returns {string} Résumé formaté
   */
  export const generatePriceSummary = (listing, language = 'fr') => {
    if (!listing || !listing.price) return 'N/A';
  
    const priceFormatted = formatPrice(listing.price);
    const range = getPriceRangeLabel(listing.price, listing.category, language);
  
    const summaries = {
      fr: {
        perNight: `${priceFormatted} par nuit - ${range}`,
        perMonth: `${priceFormatted} par mois - ${range}`,
        perSquareMeter: `${priceFormatted} par m² - ${range}`,
        service: `À partir de ${priceFormatted}`,
      },
      en: {
        perNight: `${priceFormatted} per night - ${range}`,
        perMonth: `${priceFormatted} per month - ${range}`,
        perSquareMeter: `${priceFormatted} per m² - ${range}`,
        service: `Starting from ${priceFormatted}`,
      },
    };
  
    const priceType = listing.priceType || 'service';
    return summaries[language]?.[priceType] || summaries.fr[priceType];
  };